import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";

/**
 * Support-ticket screenshot picker + gallery.
 *
 * Two rendering modes controlled by `readOnly`:
 *   • Composer (readOnly=false) — file input + preview strip. Uploads to
 *     `/support/upload`, pushes the returned path onto the parent's
 *     `paths` array via `onChange`.
 *   • Gallery  (readOnly=true) — just the thumbnails, clickable for a
 *     full-size lightbox in a new tab.
 *
 * Paths are storage keys (e.g. `yachtspa/support/…png`) resolved via
 * `fileUrl()` from the shared API helper.
 */
export default function TicketAttachments({ paths, onChange, readOnly = false, testId = "ticket-attachments" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const list = Array.isArray(paths) ? paths : [];

  const pick = () => { if (inputRef.current) inputRef.current.click(); };

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    if (list.length + files.length > 8) {
      toast.error("Up to 8 screenshots per message.");
      return;
    }
    setUploading(true);
    const added = [];
    try {
      for (const f of files) {
        if (!f.type.startsWith("image/")) {
          toast.error(`${f.name} isn't an image — skipping.`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/support/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data?.path) added.push(data.path);
      }
      if (added.length) onChange([...list, ...added]);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (p) => {
    if (readOnly) return;
    onChange(list.filter((x) => x !== p));
  };

  if (readOnly && list.length === 0) return null;

  return (
    <div data-testid={testId}>
      {list.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {list.map((p) => (
            <a
              key={p}
              href={fileUrl(p)}
              target="_blank"
              rel="noreferrer"
              className="relative group block border border-border bg-muted/40 overflow-hidden hover:border-accent transition-colors"
              style={{ width: 84, height: 84 }}
              data-testid={`${testId}-thumb-${p.split("/").pop()}`}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={fileUrl(p)}
                alt="screenshot"
                className="w-full h-full object-cover"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
              {!readOnly && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(p); }}
                  className="absolute top-1 right-1 h-5 w-5 rounded-full bg-background/90 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`${testId}-remove`}
                  aria-label="Remove screenshot"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </a>
          ))}
        </div>
      )}
      {!readOnly && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => upload(Array.from(e.target.files || []))}
            data-testid={`${testId}-input`}
          />
          <button
            type="button"
            onClick={pick}
            disabled={uploading || list.length >= 8}
            className="inline-flex items-center gap-2 text-xs text-accent hover:text-accent/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid={`${testId}-add`}
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
            {uploading ? "Uploading…" : list.length === 0 ? "Attach screenshots" : `Add more (${list.length}/8)`}
          </button>
        </>
      )}
    </div>
  );
}
